import { Button, Divider, Drawer, Form, Input, Space } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import Select from "../../../components/generic/Select";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { addEditTeam } from "../../../store/team/thunks";

const AddEditTeamDrawer = ({
  branchesOptions,
  topics,
  usersOptions,
  handleClose,
  selectedTeam,
  selectedTenantId,
}) => {
  const { isEdit, isView } = useMemo(
    () => ({
      isEdit: !!selectedTeam?.id,
      isView: selectedTeam?.isView,
    }),
    [selectedTeam]
  );
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);

  // Watch form values
  const selectedBranchId = Form.useWatch("branch_id", form);
  const selectedMemberIds = Form.useWatch("team_members", form);
  const selectedTeamLeaderId = Form.useWatch("team_leader_id", form);

  useEffect(() => {
    if (selectedTeam) {
      const team_leader_id = selectedTeam?.teamLead?.id;
      form.setFieldsValue({
        ...selectedTeam,
        team_leader_id,
        team_members: selectedTeam?.team_members?.filter(
          ({ user_id }) => selectedTeam?.teamLead?.id !== user_id
        ),
      });
    }
  }, [selectedTeam, form]);

  // Filter topics based on selected branch
  const topicsOptions = useMemo(() => {
    if (!selectedBranchId) return [];
    return (
      topics?.filter((t) => t?.branch_ids?.includes(selectedBranchId)) || []
    ).map(({ name, id }) => ({ label: name, value: id }));
  }, [topics, selectedBranchId]);

  // Filter users to exclude already selected ones
  const filteredUsersOptions = useMemo(() => {
    const selectedUserIds = [
      selectedTeamLeaderId,
      ...(selectedMemberIds?.map((member) => member?.user_id) || []),
    ].filter(Boolean);

    return (
      usersOptions?.map((user) => ({
        ...user,
        disabled: selectedUserIds.includes(user.value),
      })) || []
    );
  }, [usersOptions, selectedMemberIds, selectedTeamLeaderId]);

  const handleBranchChange = useCallback(() => {
    form.setFieldsValue({ topic_ids: [] });
  }, [form]);

  const onSubmit = useCallback(
    async (values) => {
      try {
        const payload = {
          ...values,
          tenant_id: selectedTenantId,
          team_id: selectedTeam?.id || undefined,
          team_members: [
            {
              user_id: values?.team_leader_id,
              role: "Team Leader",
            },
            ...(values?.team_members || []),
          ],
        };
        setSubmitting(true);
        await dispatch(addEditTeam(payload));
        handleClose();
      } catch (error) {
        console.log(
          "🚀 ~ AddEditTeamDrawer.jsx:64 ~ AddEditTeamDrawer ~ error:",
          error
        );
      } finally {
        setSubmitting(false);
      }
    },
    [selectedTeam, selectedTenantId]
  );

  return (
    <Drawer
      title={isView ? "View Team" : isEdit ? "Edit Team" : "Create Team"}
      open
      size={480}
      footer={
        <Button
          className="w-100"
          type="primary"
          htmlType="submit"
          disabled={submitting || isView}
          loading={submitting}
          onClick={() => form.submit()}
        >
          Save Team
        </Button>
      }
      onClose={handleClose}
      closable={!submitting}
      maskClosable={!submitting}
      keyboard={!submitting}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={onSubmit}
        disabled={submitting || isView}
      >
        <Form.Item name="name" label="Team Name" rules={[{ required: true }]}>
          <Input placeholder="Enter team name" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="branch_id" label="Branch" rules={[{ required: true }]}>
          <Select
            placeholder="Select branch"
            options={branchesOptions}
            onChange={handleBranchChange}
          />
        </Form.Item>

        <Form.Item
          name="topic_ids"
          label="Topics"
          rules={[
            {
              required: true,
              message: "Please select at least one topic",
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="Select topics"
            options={topicsOptions}
          />
        </Form.Item>

        <Form.Item
          name="team_leader_id"
          label="Team Lead"
          rules={[{ required: true }]}
        >
          <Select
            placeholder="Select team lead"
            options={filteredUsersOptions}
          />
        </Form.Item>

        <Divider />

        <Form.List name="team_members">
          {(fields, { add, remove }) => (
            <>
              <h4 style={{ marginBottom: "8px" }}>Team Members</h4>
              {fields.map(({ key, name }) => (
                <Space key={key} style={{ display: "flex" }} align="start">
                  <Form.Item
                    name={[name, "user_id"]}
                    rules={[{ required: true, message: "Select user" }]}
                  >
                    <Select
                      placeholder="User"
                      style={{ width: 190 }}
                      options={filteredUsersOptions}
                    />
                  </Form.Item>

                  <Form.Item
                    name={[name, "role"]}
                    style={{ width: 190 }}
                    rules={[{ required: true, message: "Enter Role" }]}
                  >
                    <Input placeholder="Role" />
                  </Form.Item>

                  <Button
                    className="delete-btn"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(name)}
                  />
                </Space>
              ))}

              <Button type="dashed" block onClick={() => add()}>
                + Add Member
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Drawer>
  );
};

export default AddEditTeamDrawer;
